import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import ThemePreviewModal from '@/pages/superadmin/themes/components/ThemePreviewModal';

// ============================================================
// Espace « Développeur » (typecompte 8).
// Un compte Développeurs n'accède QU'À son espace de création de
// thèmes : il voit uniquement les thèmes qu'il a générés et peut
// fixer librement leur prix de vente.
// ============================================================

interface DevTheme {
  id: number;
  titre: string;
  description: string | null;
  typetheme: string | null;
  prix: string | null;
  active: number | null;
  version: string | null;
  imagecouverture: string | null;
  stylesheet: string | null;
}

const EMPTY_FORM = { titre: '', description: '', typetheme: 'Boutique', prix: '0' };

export default function ThemeDevPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [themes, setThemes] = useState<DevTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingPrice, setEditingPrice] = useState<{ id: number; value: string } | null>(null);
  const [previewTheme, setPreviewTheme] = useState<DevTheme | null>(null);
  const [testThemeId, setTestThemeId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchErr } = await supabase
        .from('sitewebtheme')
        .select('id, titre, description, typetheme, prix, active, version, imagecouverture, stylesheet')
        .eq('idauteur', user.id)
        .order('id', { ascending: false });
      if (fetchErr) throw fetchErr;
      setThemes((data as DevTheme[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!user || !form.titre.trim()) return;
    setSaving(true);
    setError('');
    try {
      const { error: insErr } = await supabase.from('sitewebtheme').insert({
        idauteur: user.id,
        titre: form.titre.trim(),
        description: form.description.trim(),
        typetheme: form.typetheme,
        prix: form.prix || '0',
        version: '1.0',
        active: 0,
      });
      if (insErr) throw insErr;
      setForm(EMPTY_FORM);
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const savePrice = async () => {
    if (!editingPrice) return;
    try {
      const { error: upErr } = await supabase
        .from('sitewebtheme')
        .update({ prix: editingPrice.value || '0' })
        .eq('id', editingPrice.id);
      if (upErr) throw upErr;
      setThemes((prev) => prev.map((t) => (t.id === editingPrice.id ? { ...t, prix: editingPrice.value } : t)));
      setEditingPrice(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour du prix');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const { error: delErr } = await supabase.from('sitewebtheme').delete().eq('id', id);
      if (delErr) throw delErr;
      setThemes((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de suppression');
    }
  };

  const inputClass = 'w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-300 transition-colors';

  const openTestShop = () => {
    if (themes.length === 0) return;
    const chosen = themes.find((t) => t.id === testThemeId) || themes[0];
    setPreviewTheme(chosen);
  };

  return (
    <div className="min-h-screen bg-background-100">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-code-s-slash-line text-white text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-bold font-heading text-foreground-950 leading-tight">Espace Développeur</p>
              <p className="text-[11px] text-foreground-500 leading-tight">{user?.name}</p>
            </div>
          </div>
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line"></i>Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Mes thèmes</h1>
            <p className="text-sm text-foreground-500 mt-1">Créez et fixez le prix de vos thèmes. Vous ne voyez que vos propres créations.</p>
          </div>
          <button
            onClick={() => setShowForm((v) => !v)}
            className="flex items-center gap-2 px-5 py-2.5 bg-foreground-950 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>Nouveau thème
          </button>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
            <i className="ri-error-warning-line"></i>{error}
          </div>
        )}

        {/* Boutique test : prévisualisation en direct d'un thème */}
        <div className="mb-6 bg-accent-50/70 border border-accent-200/60 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-store-3-line text-xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground-900">Boutique test</p>
            <p className="text-xs text-foreground-500 mt-0.5">Prévisualisez le rendu réel d&apos;un de vos thèmes sur une boutique de démonstration, avant de le publier.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <select
              value={testThemeId ?? ''}
              onChange={(e) => setTestThemeId(e.target.value ? Number(e.target.value) : null)}
              disabled={themes.length === 0}
              className="h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-700 focus:outline-none focus:border-primary-300 cursor-pointer disabled:opacity-50 max-w-[180px]"
            >
              {themes.length === 0 && <option value="">Aucun thème</option>}
              {themes.map((t) => <option key={t.id} value={t.id}>{t.titre}</option>)}
            </select>
            <button
              onClick={openTestShop}
              disabled={themes.length === 0}
              className="flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              <i className="ri-eye-line"></i>Ouvrir
            </button>
          </div>
        </div>

        {showForm && (
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 mb-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-foreground-600 mb-1">Titre du thème</label>
                <input type="text" value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder="Ex : Minimal Éditorial" className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Catégorie</label>
                <select value={form.typetheme} onChange={(e) => setForm({ ...form, typetheme: e.target.value })} className={`${inputClass} cursor-pointer`}>
                  {['Boutique', 'Services', 'Restauration', 'Mode', 'Beauté', 'Artiste', 'Portfolio', 'Formation', 'Immobilier'].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Prix de vente (MAD)</label>
                <input type="number" min="0" value={form.prix} onChange={(e) => setForm({ ...form, prix: e.target.value })} className={inputClass} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Décrivez le style et les points forts du thème" className={`${inputClass} resize-none`} />
            </div>
            <div className="flex gap-3">
              <button onClick={handleCreate} disabled={saving || !form.titre.trim()} className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap transition-colors">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line mr-1.5"></i>}Créer le thème
              </button>
              <button onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }} className="px-4 py-2.5 bg-background-100 text-foreground-600 rounded-full text-sm cursor-pointer hover:bg-background-200/70 whitespace-nowrap transition-colors">Annuler</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : themes.length === 0 ? (
          <div className="text-center py-20 bg-background-50 border border-background-200/70 rounded-xl">
            <i className="ri-palette-line text-4xl text-foreground-300 block mb-3"></i>
            <p className="text-foreground-600 font-medium">Aucun thème pour l’instant</p>
            <p className="text-xs text-foreground-400 mt-1">Créez votre premier thème pour commencer.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {themes.map((t) => (
              <div key={t.id} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden flex flex-col">
                <div className="h-36 bg-background-100 overflow-hidden">
                  {t.imagecouverture ? (
                    <img src={t.imagecouverture} alt={t.titre} className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-foreground-300"><i className="ri-palette-line text-3xl"></i></div>
                  )}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-foreground-900">{t.titre}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${t.active ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'}`}>
                      {t.active ? 'Publié' : 'Brouillon'}
                    </span>
                  </div>
                  {t.typetheme && <span className="text-[11px] text-foreground-400 mt-1">{t.typetheme}</span>}
                  <p className="text-xs text-foreground-500 line-clamp-2 mt-2 flex-1">{t.description || 'Aucune description'}</p>

                  <div className="mt-3 pt-3 border-t border-background-200/70">
                    {editingPrice?.id === t.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          value={editingPrice.value}
                          onChange={(e) => setEditingPrice({ id: t.id, value: e.target.value })}
                          className="flex-1 px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm"
                          autoFocus
                        />
                        <button onClick={savePrice} className="px-3 py-2 bg-primary-500 text-background-50 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap">OK</button>
                        <button onClick={() => setEditingPrice(null)} className="px-2 py-2 bg-background-100 text-foreground-600 rounded-full text-xs cursor-pointer"><i className="ri-close-line"></i></button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingPrice({ id: t.id, value: t.prix || '0' })}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                      >
                        <span className="text-xs text-foreground-500 flex items-center gap-1.5"><i className="ri-price-tag-3-line"></i>Prix de vente</span>
                        <span className="text-sm font-bold text-primary-600">{parseInt(t.prix || '0', 10).toLocaleString()} MAD</span>
                      </button>
                    )}
                  </div>

                  <button
                    onClick={() => setPreviewTheme(t)}
                    className="mt-2 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium text-foreground-600 bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <i className="ri-eye-line"></i>Prévisualiser
                  </button>

                  <button
                    onClick={() => handleDelete(t.id)}
                    className="mt-2 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-8 text-[11px] text-foreground-400 flex items-center gap-1.5">
          <i className="ri-information-line"></i>
          Espace réservé aux comptes Développeurs : création de thèmes et fixation des prix uniquement.
        </p>
      </main>

      {previewTheme && (
        <ThemePreviewModal
          themeId={previewTheme.id}
          title={previewTheme.titre}
          stylesheet={previewTheme.stylesheet || ''}
          onClose={() => setPreviewTheme(null)}
        />
      )}
    </div>
  );
}